import { useCallback } from 'react';
import { useNoticeBannersStackContext } from '@sg/garnish';

import { TierName, useRejoinSubscriptionMutation } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

export const useReactivateSweetpassMembership = (
  props: ReactivateSweetpassMembershipProps,
) => {
  const { subscriptionId, refetchMembership } = props;

  const { track } = useTelemetry();
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const [, rejoinSubscriptionMutation] = useRejoinSubscriptionMutation();

  const reactivateSubscription = useCallback(async () => {
    if (!subscriptionId) return;

    const { data: rejoinSubscriptionData } = await rejoinSubscriptionMutation({
      subscriptionId,
    });

    const hasFailedToReactivate =
      rejoinSubscriptionData?.rejoinSubscription?.__typename !==
      'RejoinSubscriptionSuccessResponse';

    const isCannotReactivate =
      rejoinSubscriptionData?.rejoinSubscription?.__typename ===
      'SubscriptionCannotBeReactivatedError';

    if (isCannotReactivate) {
      addNoticeBanner({
        text: t('sweetpass.subscription.cannot-reactivate'),
        palette: 'caution',
      });

      return;
    }

    if (hasFailedToReactivate) {
      addNoticeBanner({
        text: t(
          'account.sweetpass-membership.membership-level.rejoin-sweetpass-plus.notice.failure',
        ),
        palette: 'caution',
      });

      return;
    }

    addNoticeBanner({
      text: t(
        'account.sweetpass-membership.membership-level.rejoin-sweetpass-plus.notice.success',
      ),
      palette: 'success',
    });

    track('sweetpass.tier.changed', { loyaltyTier: TierName.Sweetpass });
    refetchMembership();
  }, [
    subscriptionId,
    rejoinSubscriptionMutation,
    addNoticeBanner,
    track,
    refetchMembership,
    t,
  ]);

  return { reactivateSubscription };
};

type ReactivateSweetpassMembershipProps = {
  readonly subscriptionId?: string;
  readonly refetchMembership: () => void;
};
